import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../userContext';
import CampaignsCard from '../partials/campaigns/CampaignsCard';
import DefaultLoading from '../common/DefaultLoading';
import { fetchCampaignsByUserAndStatus } from '../data/campaignService';
import DashboardCard04 from './DashboardChart';
import GetStartedWizard from './GetStartedWizard';

function DashboardSkeleton() {
  return (
    <div className="max-w-7xl mx-auto" style={{fontFamily: '"Share Tech Mono", monospace'}}>
      {/* Header Skeleton */}
      <div className="mb-8">
        <div className="w-48 h-10 rounded-lg animate-pulse" style={{
          background: 'rgba(241, 196, 15, 0.1)',
          border: '1px solid rgba(241, 196, 15, 0.3)'
        }} />
      </div>

      {/* Chart and Quick Create Grid */}
      <div className="grid grid-cols-12 gap-6 mb-8">
        {/* Chart Card Skeleton */}
        <div className="col-span-12 sm:col-span-8">
          <div className="studio-card rounded-xl p-6">
            <div className="space-y-4">
              <div className="h-6 w-1/4 rounded animate-pulse" style={{
                background: 'rgba(241, 196, 15, 0.2)'
              }} />
              <div className="h-[300px] rounded animate-pulse" style={{
                background: 'rgba(52, 73, 94, 0.4)',
                border: '1px solid rgba(241, 196, 15, 0.2)'
              }} />
            </div>
          </div>
        </div>
        
        {/* Quick Create Skeleton */}
        <div className="col-span-12 sm:col-span-4">
          <div className="studio-card rounded-xl p-5">
            <div className="space-y-4">
              <div className="h-4 w-24 rounded animate-pulse" style={{
                background: 'rgba(241, 196, 15, 0.2)'
              }} />
              <div className="h-10 rounded-md animate-pulse" style={{
                background: 'rgba(231, 76, 60, 0.3)',
                border: '1px solid rgba(241, 196, 15, 0.4)'
              }} />
              <div className="h-4 w-20 rounded animate-pulse mt-6" style={{
                background: 'rgba(241, 196, 15, 0.2)'
              }} />
              <div className="space-y-3">
                <div className="h-12 rounded-lg animate-pulse" style={{
                  background: 'rgba(52, 73, 94, 0.4)',
                  border: '1px solid rgba(241, 196, 15, 0.2)'
                }} />
                <div className="h-12 rounded-lg animate-pulse" style={{
                  background: 'rgba(52, 73, 94, 0.4)',
                  border: '1px solid rgba(241, 196, 15, 0.2)'
                }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Live Campaigns Section Skeleton */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-24 h-10 rounded animate-pulse" style={{
            background: 'rgba(241, 196, 15, 0.2)'
          }} />
          <div className="w-8 h-8 rounded-full animate-pulse" style={{
            background: 'rgba(231, 76, 60, 0.3)',
            border: '1px solid rgba(241, 196, 15, 0.4)'
          }} />
        </div>
      </div>

      {/* Campaign Cards Grid Skeleton */}
      <div className="grid grid-cols-12 gap-6">
        {[1, 2, 3].map((index) => (
          <div key={index} className="col-span-12 sm:col-span-6 lg:col-span-4">
            <div className="studio-card rounded-xl p-5">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex justify-between items-center">
                  <div className="w-32 h-4 rounded animate-pulse" style={{
                    background: 'rgba(241, 196, 15, 0.2)'
                  }} />
                  <div className="w-8 h-8 rounded-full animate-pulse" style={{
                    background: 'rgba(52, 73, 94, 0.4)'
                  }} />
                </div>
                
                {/* Title */}
                <div className="w-3/4 h-6 rounded animate-pulse" style={{
                  background: 'rgba(241, 196, 15, 0.3)'
                }} />
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="h-16 rounded animate-pulse" style={{
                    background: 'rgba(52, 73, 94, 0.4)'
                  }} />
                  <div className="h-16 rounded animate-pulse" style={{
                    background: 'rgba(52, 73, 94, 0.4)'
                  }} />
                </div>
                
                {/* Footer */}
                <div className="flex justify-between items-center">
                  <div className="w-20 h-4 rounded animate-pulse" style={{
                    background: 'rgba(241, 196, 15, 0.2)'
                  }} />
                  <div className="w-24 h-4 rounded animate-pulse" style={{
                    background: 'rgba(241, 196, 15, 0.2)'
                  }} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard() {
  const { user, firstName, businessProfile, campaigns: contextCampaigns } = useUser();
  const [campaigns, setCampaigns] = useState([]);
  const [isShowingAll, setIsShowingAll] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [campaignIds, setCampaignIds] = useState([]);

  // OPTIMIZATION: Use campaigns from UserContext when available
  useEffect(() => {
    async function loadCampaigns() {
      try {
        // OPTIMIZATION: Use cached campaigns from context if available
        if (contextCampaigns && contextCampaigns.length >= 0) {
          console.log('Using campaigns from UserContext cache');
          
          // Filter live campaigns from context
          const liveCampaignsData = contextCampaigns.filter(campaign => campaign.status === 'live');
          const completeCampaignsData = contextCampaigns.filter(campaign => campaign.status === 'complete');

          // Sort campaigns by creation date (most recent first)
          const sortedLiveCampaigns = liveCampaignsData.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );

          setCampaigns(sortedLiveCampaigns);
          setCampaignIds([...liveCampaignsData, ...completeCampaignsData].map(campaign => campaign.id));
          setIsLoading(false);
          return;
        }

        // Fallback to direct API calls only if context data is not available
        console.log('Fetching campaigns directly from API');
        const liveCampaignsData = await fetchCampaignsByUserAndStatus(businessProfile.id, 'live');
        const completeCampaignsData = await fetchCampaignsByUserAndStatus(businessProfile.id, 'complete');

        // Sort campaigns by creation date (most recent first)
        const sortedLiveCampaigns = liveCampaignsData.sort(
          (a, b) => b.createdAt.toDate() - a.createdAt.toDate()
        );

        setCampaigns(sortedLiveCampaigns);
        setCampaignIds([...liveCampaignsData, ...completeCampaignsData].map(campaign => campaign.id));

      } catch (error) {
        console.error('Error loading campaigns: ', error);
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    }

    if (user && businessProfile?.id) {
      loadCampaigns();
    }
  }, [user, businessProfile, contextCampaigns, navigate]);

  // OPTIMIZATION: Update campaigns when context changes
  useEffect(() => {
    if (contextCampaigns && contextCampaigns.length >= 0) {
      const liveCampaignsData = contextCampaigns.filter(campaign => campaign.status === 'live');
      const completeCampaignsData = contextCampaigns.filter(campaign => campaign.status === 'complete');

      const sortedLiveCampaigns = liveCampaignsData.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      setCampaigns(sortedLiveCampaigns);
      setCampaignIds([...liveCampaignsData, ...completeCampaignsData].map(campaign => campaign.id));
    }
  }, [contextCampaigns]);

  const liveCampaignsCount = campaigns.length;
  const displayedCampaigns = isShowingAll ? campaigns : campaigns.slice(0, 3);

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="max-w-7xl mx-auto" style={{fontFamily: '"Share Tech Mono", monospace'}}>
      {/* Header with Gaming Style */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-black tracking-wider flex items-center gap-3" style={{
          color: '#F1C40F',
          textShadow: '2px 2px 0px #E67E22, 4px 4px 0px #D35400',
          WebkitTextStroke: '1px #E67E22'
        }}>
          DASHBOARD
        </h1>
        <p className="text-sm mt-2 font-bold" style={{color: '#ECF0F1'}}>
          Welcome back! Manage your campaigns and track performance
        </p>
      </div>

      {/* Get Started Wizard */}
      {campaigns.length === 0 && (
        <GetStartedWizard />
      )}

      <div className="grid grid-cols-12 gap-6 mb-8">
        {/* Chart Card */}
        <div className="col-span-12 sm:col-span-8 h-full">
          <div className="studio-card rounded-xl h-full overflow-hidden">
            <DashboardCard04 campaignIds={campaignIds} />
          </div>
        </div>
        
        {/* Quick Create Card */}
        <div className="col-span-12 sm:col-span-4 h-full">
          <div className="studio-card rounded-xl pl-5 pt-5 pb-7 h-full flex flex-col" style={{fontFamily: '"Share Tech Mono", monospace'}}>
            <div className="text-xs uppercase font-black tracking-wider flex items-center gap-2" style={{color: '#F1C40F'}}>
              🚀 QUICK CREATE
            </div>

            <div className="flex-grow flex flex-col min-h-0 mt-4">
              {/* New Campaign Button */}
              <div className="pr-5 pb-4">
                <button
                  onClick={() => navigate('/studio/new')}
                  className="retro-button block w-full text-center font-bold text-white px-4 py-3 rounded-md transition-all duration-100"
                  style={{
                    background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                    border: '2px solid #F1C40F',
                    textShadow: '1px 1px 0px #A93226'
                  }}
                >
                  🎮 NEW CAMPAIGN
                </button>
              </div>

              {/* Quick Actions & Recent Activity */}
              <div className="pr-5 flex-grow">
                <div className="text-xs uppercase font-black tracking-wider flex items-center gap-2 mb-4" style={{color: '#F1C40F'}}>
                  ⚡ QUICK ACTIONS
                </div>
                
                <div className="space-y-3">
                  {/* Quick Link to View All Campaigns */}
                  <button
                    onClick={() => navigate('/studio/campaigns')}
                    className="w-full flex justify-between items-center p-3 rounded-lg transition-all duration-100 hover:shadow-md"
                    style={{
                      background: 'rgba(52, 73, 94, 0.4)',
                      border: '1px solid rgba(241, 196, 15, 0.3)'
                    }}
                  >
                    <span className="text-sm font-bold" style={{color: '#ECF0F1'}}>📋 View All Campaigns</span>
                    <span className="text-xs" style={{color: '#F1C40F'}}>→</span>
                  </button>
                  
                  {/* Quick Link to Game Library */}
                  <button
                    onClick={() => navigate('/studio/games')}
                    className="w-full flex justify-between items-center p-3 rounded-lg transition-all duration-100 hover:shadow-md"
                    style={{
                      background: 'rgba(52, 73, 94, 0.4)',
                      border: '1px solid rgba(241, 196, 15, 0.3)'
                    }}
                  >
                    <span className="text-sm font-bold" style={{color: '#ECF0F1'}}>🎮 Game Library</span>
                    <span className="text-xs" style={{color: '#F1C40F'}}>→</span>
                  </button>

                  {/* Quick Tip or Status */}
                  <div className="p-3 rounded-lg" style={{
                    background: 'rgba(46, 204, 113, 0.1)',
                    border: '1px solid rgba(46, 204, 113, 0.3)'
                  }}>
                    <div className="flex items-start gap-2">
                      <span className="text-lg">💡</span>
                      <div>
                        <div className="text-xs font-bold mb-1" style={{color: '#2ECC71'}}>
                          PRO TIP
                        </div>
                        <div className="text-xs font-bold leading-relaxed" style={{color: '#ECF0F1'}}>
                          {campaigns.length === 0 
                            ? "Start with a Quiz game - they're the easiest to set up and get great engagement!"
                            : liveCampaignsCount === 0
                            ? "Set your campaigns live to start collecting player data and insights!"
                            : "Check your analytics regularly to optimize your campaigns for better performance!"
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Live Campaigns Section */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl md:text-3xl font-black tracking-wider" style={{
            color: '#F1C40F',
            textShadow: '1px 1px 0px #E67E22',
            WebkitTextStroke: '0.5px #E67E22'
          }}>
            LIVE CAMPAIGNS
          </h2>
          <span className="inline-flex items-center justify-center px-3 py-1 text-sm font-bold leading-none text-white rounded-full" style={{
            background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
            border: '2px solid #F1C40F',
            textShadow: '1px 1px 0px #A93226'
          }}>
            {liveCampaignsCount}
          </span>
        </div>
        {liveCampaignsCount > 3 && !isShowingAll && (
          <button
            className="retro-button flex items-center gap-2 px-6 py-3 font-bold text-white transition-all duration-100"
            style={{
              background: 'linear-gradient(135deg, #F1C40F, #F39C12)',
              border: '2px solid #E67E22',
              borderRadius: '8px',
              color: '#2C3E50',
              textShadow: '1px 1px 0px #E67E22'
            }}
            onClick={() => setIsShowingAll(true)}
          >
            <span className="text-sm">🎯 SHOW ALL</span>
          </button>
        )}
      </div>

      {campaigns.length > 0 ? (
        <div className="grid grid-cols-12 gap-6">
          {displayedCampaigns.map((campaign) => (
            <CampaignsCard key={campaign.id} campaign={campaign} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 studio-card rounded-xl">
          <div className="text-6xl mb-4">🎮</div>
          <h3 className="text-xl font-bold mb-2" style={{color: '#F1C40F'}}>
            NO LIVE CAMPAIGNS
          </h3>
          <p className="font-bold" style={{color: '#ECF0F1'}}>
            Ready to launch your first interactive campaign and start engaging customers!
          </p>
          <button
            className="retro-button mt-6 px-8 py-4 font-bold text-white transition-all duration-100"
            style={{
              background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
              border: '2px solid #F1C40F',
              borderRadius: '8px',
              textShadow: '1px 1px 0px #A93226'
            }}
            onClick={() => navigate('/studio/new')}
          >
            🚀 CREATE CAMPAIGN
          </button>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
